import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@time-theft-analyzer/shared";
import { axiosInstance } from "@time-theft-analyzer/shared";
import {
  ChartBarIcon,
  UserGroupIcon,
  ClockIcon,
  CurrencyDollarIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  Square3Stack3DIcon,
  BellIcon,
  CubeIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  CommandLineIcon,
  CpuChipIcon,
  ArrowRightIcon,
  ArrowRightStartOnRectangleIcon,
  MagnifyingGlassIcon,
  AcademicCapIcon,
  ChevronRightIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";
import { quickLinksForApp } from "./appQuickLinks";
import { UserLanguagePicker } from "../i18n/UserLanguagePicker";
import { formatNumber } from "../i18n/format";
import { useTranslation } from "react-i18next";
import McpKeyModal from "./McpKeyModal";

interface AppCard {
  _id: string;
  // NOTE: the `/api/applications` response has no `id` field — the slug lives in `name`.
  // Kept optional only for backwards-compat; nothing should rely on it.
  id?: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  route: string;
  color: string;
  userAccessLevel?: string;
  enabled: boolean;
}

// Gradient configs per app color for visual diversity
const gradientMap: Record<string, { from: string; to: string; shadow: string; light: string; strip: string }> = {
  blue:   { from: "from-blue-500",    to: "to-indigo-600",  shadow: "shadow-blue-500/25",    light: "bg-blue-50",    strip: "from-blue-500 to-indigo-600" },
  indigo: { from: "from-indigo-500",  to: "to-purple-600",  shadow: "shadow-indigo-500/25",  light: "bg-indigo-50",  strip: "from-indigo-500 to-purple-600" },
  purple: { from: "from-purple-500",  to: "to-fuchsia-500", shadow: "shadow-purple-500/25",  light: "bg-purple-50",  strip: "from-purple-500 to-fuchsia-500" },
  green:  { from: "from-emerald-500", to: "to-teal-600",    shadow: "shadow-emerald-500/25", light: "bg-emerald-50", strip: "from-emerald-500 to-teal-600" },
  red:    { from: "from-rose-500",    to: "to-pink-600",    shadow: "shadow-rose-500/25",    light: "bg-rose-50",    strip: "from-rose-500 to-pink-600" },
  yellow: { from: "from-yellow-400",  to: "to-orange-500",  shadow: "shadow-yellow-500/25",  light: "bg-yellow-50",  strip: "from-yellow-400 to-orange-500" },
  orange: { from: "from-orange-500",  to: "to-amber-600",   shadow: "shadow-orange-500/25",  light: "bg-orange-50",  strip: "from-orange-500 to-amber-600" },
  teal:   { from: "from-teal-500",    to: "to-cyan-600",    shadow: "shadow-teal-500/25",    light: "bg-teal-50",    strip: "from-teal-500 to-cyan-600" },
  violet: { from: "from-violet-500",  to: "to-purple-600",  shadow: "shadow-violet-500/25",  light: "bg-violet-50",  strip: "from-violet-500 to-purple-600" },
  amber:  { from: "from-amber-500",   to: "to-orange-600",  shadow: "shadow-amber-500/25",   light: "bg-amber-50",   strip: "from-amber-500 to-orange-600" },
};

const defaultGradient = { from: "from-gray-500", to: "to-slate-600", shadow: "shadow-gray-500/25", light: "bg-gray-50", strip: "from-gray-500 to-slate-600" };

const AppSelector: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation('common');
  const [apps, setApps] = useState<AppCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [mcpKeyOpen, setMcpKeyOpen] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axiosInstance.get("/api/applications");

      if (response.data.success) {
        const iconMap: Record<string, React.ComponentType<any>> = {
          ClockIcon,
          UserGroupIcon,
          ShieldCheckIcon,
          ChartBarIcon,
          CurrencyDollarIcon,
          Square3Stack3DIcon,
          BellIcon,
          UsersIcon: UserGroupIcon,
          CubeIcon,
          ClipboardDocumentListIcon,
          DocumentTextIcon,
          CommandLineIcon,
          CpuChipIcon,
          AcademicCapIcon,
        };

        const processedApps = response.data.applications.map((app: any) => ({
          ...app,
          icon: iconMap[app.icon] || Square3Stack3DIcon,
          color: app.color || "blue",
        }));

        setApps(processedApps);
      }
    } catch (error) {
      console.error("Failed to fetch applications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAppClick = (app: AppCard) => {
    navigate(app.route);
  };

  const filteredApps = apps.filter(
    (app) =>
      app.name.toLowerCase().includes(search.toLowerCase()) ||
      app.description.toLowerCase().includes(search.toLowerCase())
  );

  const firstName = user?.name?.split(" ")[0] || t('appSelector.fallbackName');

  if (loading) {
    return (
      <div className="min-h-screen bg-tk-base flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-tk-border border-t-tk-accent" />
            <div
              className="animate-spin rounded-full h-10 w-10 border-2 border-transparent border-b-tk-violet absolute inset-0"
              style={{ animationDirection: "reverse", animationDuration: "1.5s" }}
            />
          </div>
          <p className="text-sm text-tk-text-muted">{t('appSelector.loadingWorkspace')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tk-base relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-indigo-400/[0.04] blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-violet-400/[0.04] blur-3xl" />
      <div className="absolute top-1/2 right-0 w-64 h-64 rounded-full bg-blue-400/[0.03] blur-3xl" />

      {/* Top navigation bar */}
      <div
        className="sticky top-0 z-30 border-b border-tk-border/80"
        style={{ background: "rgba(255,255,255,0.85)", backdropFilter: "blur(16px) saturate(180%)" }}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-tk-accent to-tk-violet flex items-center justify-center shadow-sm">
              <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <span className="font-semibold text-tk-text text-sm tracking-tight">{t('appSelector.brand')}</span>
          </div>

          <div className="flex items-center gap-3">
            {/* User info */}
            <div className="hidden sm:flex items-center gap-2.5">
              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-tk-accent/25 to-tk-violet/25 border-2 border-white shadow-sm flex items-center justify-center">
                <span className="text-[10px] font-bold text-tk-text">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-sm text-tk-text-secondary font-medium">{user?.name}</span>
                {user?.email && (
                  <span className="text-[11px] text-tk-text-muted truncate max-w-[180px]">{user.email}</span>
                )}
              </div>
            </div>
            <div className="w-px h-5 bg-tk-border hidden sm:block" />
            <UserLanguagePicker variant="inline" className="hidden sm:flex items-center gap-2" />
            <div className="w-px h-5 bg-tk-border hidden sm:block" />
            <button
              onClick={() => setMcpKeyOpen(true)}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-sm font-medium text-tk-text-secondary hover:bg-tk-base hover:text-tk-accent transition-all duration-200"
              title={t('mcpKey.navButton')}
            >
              <KeyIcon className="h-4 w-4" />
              <span className="hidden md:inline">{t('mcpKey.navButton')}</span>
            </button>
            <div className="w-px h-5 bg-tk-border hidden sm:block" />
            <button
              onClick={() => logout()}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-sm font-medium text-tk-text-secondary hover:bg-red-50 hover:text-red-600 transition-all duration-200"
            >
              <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{t('auth.logout')}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 relative z-10">
        {/* Hero header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 p-8 sm:p-10 mb-8">
          {/* Decorative elements */}
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute top-0 right-0 w-48 h-48 opacity-20" style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "12px 12px",
          }} />
          <div className="absolute bottom-6 right-8 w-16 h-16 border-2 border-white/10 rounded-2xl rotate-12 hidden sm:block" />
          <div className="absolute top-8 right-1/3 w-2.5 h-2.5 bg-white/15 rounded-full hidden sm:block" />

          <div className="relative z-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
              <div>
                <p className="text-indigo-200 text-sm font-medium mb-2">{t('appSelector.greetingSuffix', { greeting: t(getGreetingKey()) })}</p>
                <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                  {firstName}
                </h1>
                <p className="text-white/60 text-base mt-2 max-w-lg">
                  {apps.length > 0
                    ? t('appSelector.subtitleReady')
                    : t('appSelector.subtitleNoAccess')}
                </p>
              </div>

              {/* Quick stats */}
              {apps.length > 0 && (
                <div className="flex gap-6 sm:gap-8">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white font-mono-tk">{formatNumber(apps.length)}</p>
                    <p className="text-indigo-200 text-[11px] font-medium uppercase tracking-wider mt-0.5">{t('appSelector.statApps')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white font-mono-tk capitalize">
                      {user?.role === "admin"
                        ? t('appSelector.roleAdmin')
                        : user?.role === "manager"
                          ? t('appSelector.roleManager')
                          : t('appSelector.roleUser')}
                    </p>
                    <p className="text-indigo-200 text-[11px] font-medium uppercase tracking-wider mt-0.5">{t('appSelector.statRole')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search bar */}
        {apps.length > 0 && (
          <div className="relative mb-6">
            <div className="absolute inset-y-0 start-0 ps-4 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-tk-text-muted" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('appSelector.searchPlaceholder')}
              className="w-full ps-11 pe-4 py-3 bg-white border border-tk-border rounded-2xl text-sm text-tk-text placeholder-tk-text-muted focus:outline-none focus:ring-4 focus:ring-tk-accent/10 focus:border-tk-accent/40 transition-all duration-300 shadow-sm"
            />
          </div>
        )}

        {/* App Grid */}
        {filteredApps.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredApps.map((app, index) => {
              const gradient = gradientMap[app.color] || defaultGradient;
              // Key off the app `name` slug — the API response has no `id` field.
              const quickLinks = quickLinksForApp(app);
              return (
                <div
                  key={app._id || app.name}
                  className="group relative bg-white border border-tk-border rounded-2xl overflow-hidden hover:shadow-tk-card-hover hover:-translate-y-1 transition-all duration-300"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Gradient strip */}
                  <div className={`h-1 bg-gradient-to-r ${gradient.strip} opacity-80 group-hover:opacity-100 transition-opacity duration-300`} />

                  <div className="p-5 flex gap-4">
                    {/* Main clickable region opens the app (its dashboard) */}
                    <button
                      type="button"
                      onClick={() => handleAppClick(app)}
                      className="flex-1 min-w-0 text-left focus:outline-none focus:ring-2 focus:ring-tk-accent/30 rounded-xl -m-1 p-1"
                    >
                      <div className="flex items-start gap-4">
                        {/* Icon badge */}
                        <div className={`h-11 w-11 rounded-2xl bg-gradient-to-br ${gradient.from} ${gradient.to} flex items-center justify-center shadow-lg ${gradient.shadow} flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                          <app.icon className="h-5.5 w-5.5 text-white" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="text-base font-bold text-tk-text tracking-tight truncate">
                              {app.name}
                            </h3>
                            <ArrowRightIcon className="h-4 w-4 text-tk-text-muted opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-200 flex-shrink-0" />
                          </div>
                          <p className="text-sm text-tk-text-secondary mt-1 line-clamp-2 leading-relaxed">
                            {app.description}
                          </p>
                        </div>
                      </div>

                      {/* Access level tag */}
                      {app.userAccessLevel && (
                        <div className="mt-4 pt-3.5 border-t border-tk-border/50">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-semibold uppercase tracking-wide ${gradient.light} border border-transparent`}>
                            {t('appSelector.accessTag', { level: app.userAccessLevel })}
                          </span>
                        </div>
                      )}
                    </button>

                    {/* Top sidebar destinations — deep-link straight into a page */}
                    {quickLinks.length > 0 && (
                      <div className="w-36 flex-shrink-0 border-s border-tk-border/50 ps-3 flex flex-col gap-1">
                        {quickLinks.map((link) => (
                          <button
                            key={link.route}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(link.route);
                            }}
                            className="group/link flex items-center justify-between gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-tk-text-secondary hover:bg-tk-base hover:text-tk-accent transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-tk-accent/30 text-left"
                          >
                            <span className="truncate">{link.name}</span>
                            <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity duration-150" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : apps.length > 0 && search ? (
          /* No search results */
          <div className="text-center py-20">
            <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center mx-auto mb-4">
              <MagnifyingGlassIcon className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-tk-text-secondary text-base font-medium">{t('appSelector.noResultsTitle')}</p>
            <p className="text-tk-text-muted text-sm mt-1">{t('appSelector.noResultsHint')}</p>
          </div>
        ) : apps.length === 0 ? (
          /* No access */
          <div className="bg-white border border-tk-border rounded-2xl p-12 text-center">
            <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center mx-auto mb-4">
              <LockClosedIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-bold text-tk-text mb-2">
              {t('appSelector.noAccessTitle')}
            </h3>
            <p className="text-sm text-tk-text-secondary max-w-sm mx-auto">
              {t('appSelector.noAccessBody')}
            </p>
          </div>
        ) : null}
      </div>

      <McpKeyModal isOpen={mcpKeyOpen} onClose={() => setMcpKeyOpen(false)} />
    </div>
  );
};

function getGreetingKey(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "appSelector.greetingMorning";
  if (hour < 17) return "appSelector.greetingAfternoon";
  return "appSelector.greetingEvening";
}

export default AppSelector;
